import React from 'react';
import { BarChart2, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров иска
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating
}) => {
  // Расчет текущей суммы иска
  const claimAmount = parseFloat(formData.claimAmount) || 0;
  
  // Обработчик изменения суммы иска
  const handleClaimAmountChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      claimAmount: newValue
    }));
  };
  
  // Предопределенные суммы исков для быстрого выбора
  const predefinedAmounts = [
    { value: '50000', label: '50 тыс.' },
    { value: '300000', label: '300 тыс.' },
    { value: '1000000', label: '1 млн' },
    { value: '5000000', label: '5 млн' }
  ];
  
  // Предопределенные типы судов для быстрого выбора
  const handleCourtTypeChange = (courtType) => {
    // Установить тип суда
    setFormData(prev => ({
      ...prev,
      courtType: courtType,
      // Сбросить несовместимые параметры
      claimType: courtType === 'civil' && prev.claimType === 'bankruptcy' ? 'property' : prev.claimType
    }));
  };
  
  // Предопределенные типы исков для быстрого выбора
  const handleClaimTypeChange = (claimType) => {
    setFormData(prev => ({
      ...prev,
      claimType: claimType
    }));
  };
  
  // Функция расчета
  const calculateCosts = () => {
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрый подбор параметров
      </h3>
      
      <div className="space-y-5">
        {/* Сумма иска */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="claimAmountSlider" className="text-gray-600 dark:text-gray-400">Сумма иска</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(claimAmount, 0)}</span>
          </div>
          <input
            type="range"
            id="claimAmountSlider"
            min="10000"
            max="10000000"
            step="10000"
            value={claimAmount}
            onChange={handleClaimAmountChange}
            aria-label="Сумма иска"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными суммами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedAmounts.map(amount => (
              <button
                key={amount.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, claimAmount: amount.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.claimAmount === amount.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {amount.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Тип суда */}
        <div>
          <div className="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">
            Тип суда
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleCourtTypeChange('arbitration')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.courtType === 'arbitration' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Арбитражный</div>
              <div className="text-xs opacity-75">Для юрлиц и ИП</div>
            </button>
            <button
              type="button"
              onClick={() => handleCourtTypeChange('civil')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.courtType === 'civil' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Общей юрисдикции</div>
              <div className="text-xs opacity-75">Для физ. лиц</div>
            </button>
          </div>
        </div>
        
        {/* Тип иска */}
        <div>
          <div className="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">
            Тип иска
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleClaimTypeChange('property')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.claimType === 'property' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Имущественный</div>
              <div className="text-xs opacity-75">С ценой иска</div>
            </button>
            <button
              type="button"
              onClick={() => handleClaimTypeChange('non_property')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.claimType === 'non_property' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              disabled={formData.courtType === 'arbitration'}
            >
              <div className="font-medium">Неимущественный</div>
              <div className="text-xs opacity-75">Без цены</div>
            </button>
          </div>
        </div>
        
        {/* Дополнительные опции */}
        <div>
          <div className="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">
            Учитывать в расчете
          </div>
          <div className="space-y-2">
            <label className="flex items-center p-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer">
              <input
                type="checkbox"
                name="includeLawyerFees"
                checked={formData.includeLawyerFees}
                onChange={(e) => setFormData(prev => ({ ...prev, includeLawyerFees: e.target.checked }))}
                className="form-checkbox mr-2"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Услуги юриста</span>
            </label>
            
            <label className="flex items-center p-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer">
              <input
                type="checkbox"
                name="includeExpertFees"
                checked={formData.includeExpertFees}
                onChange={(e) => setFormData(prev => ({ ...prev, includeExpertFees: e.target.checked }))}
                className="form-checkbox mr-2"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Судебная экспертиза</span>
            </label>
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateCosts}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать расходы
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;